import { api } from '@/services/api';

export enum AnalyticsEventName {
  PAGE_VIEW = 'page_view',
  FILE_DOWNLOAD = 'file_download',
  PDF_WATCH = 'pdf_watch',
  BUTTON_CLICK = 'button_click',
  FORM_SUBMIT = 'form_submit',
}

function generateUUID() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

const getSessionId = () => {
  let sessionId = localStorage.getItem('analytics_session_id');

  if (!sessionId) {
    sessionId = generateUUID();
    localStorage.setItem('analytics_session_id', sessionId);
  }

  return sessionId;
};

export const trackAnalyticsEvent = async (
  event_name: AnalyticsEventName,
  options: {
    page?: string;
    entity_type?: string;
    entity_id?: number;
    metadata?: Record<string, any>;
  } = {},
) => {
  try {
    await api.post(
      '/cms/analyticsEvent',
      {
        event_name: String(event_name),
        page: options.page ?? null,
        entity_type: options.entity_type ? String(options.entity_type) : null,
        entity_id: options.entity_id ? String(options.entity_id) : null,
        session_id: String(getSessionId()),
        metadata: JSON.stringify(options.metadata || {}),
        user_agent: String(navigator.userAgent),
        device_type: 'mobile',
        env: 'mobile',
        referrer: document.referrer ? String(document.referrer) : null,
      },
      // Tracké sur des pages publiques (signup, etc.) où l'utilisateur n'a pas
      // encore de JWT : un 401 ne doit pas déclencher de déconnexion forcée.
      { skipAuthInterceptor: true },
    );
  } catch (error) {
    console.error('Analytics error:', error);
  }
};

export const trackPageView = async (page: string) => {
  return trackAnalyticsEvent(AnalyticsEventName.PAGE_VIEW, {
    page,
  });
};

export function useAnalytics() {

  const trackEvent = async (
    event_name: AnalyticsEventName,
    options: {
      entity_type?: string;
      entity_id?: number;
      metadata?: Record<string, any>;
    } = {},
  ) => {
    return trackAnalyticsEvent(event_name, options);
  };

  /*
   |--------------------------------------------------------------------------
   | Helpers
   |--------------------------------------------------------------------------
   */

  const trackFileDownload = (
    file: {
      title?: string;
      name?: string;
      extension?: string;
      url?: string;
    },
    entityType?: string,
    entityId?: number,
  ) => {
    trackEvent(AnalyticsEventName.FILE_DOWNLOAD, {
      entity_type: entityType,
      entity_id: entityId,
      metadata: {
        title: file.title,
        file_name: file.name,
        file_extension: file.extension,
        link_url: file.url,
      },
    });
  };

  const trackPdfWatch = (
    options: {
      fileName?: string;
      durationMs: number;
      duration_seconds: number;
      percentage: number;
      pagesVisited?: number; // 👈 AJOUT
    },
    entityType?: string,
    entityId?: number,
  ) => {
    trackEvent(AnalyticsEventName.PDF_WATCH, {
      entity_type: entityType,
      entity_id: entityId,
      metadata: {
        file_name: options.fileName || '',

        // ⏱ Temps
        duration_ms: options.durationMs,
        duration_seconds: options.duration_seconds,

        // 📄 Engagement
        pages_visited: options.pagesVisited ?? 1,
        percentage_viewed: Number(options.percentage.toFixed(2)),

        // 🎯 Completion logique
        completed: options.percentage >= 90,
      },
    });
  };

  return {
    trackEvent,
    trackFileDownload,
    trackPdfWatch,
  };
}
